import { getContent, IContentContext } from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';

/**
 * POS-8856: Pre-resolved, plain-string labels for the retail invoice PDF.
 *
 * The invoice PDF is rendered imperatively via `pdf(...).toBlob()` / `<PDFViewer>`,
 * which renders OUTSIDE the React ContentContext provider tree — so `<Content>` and
 * `useContent` cannot resolve inside the PDF component. Instead, callers (which DO have
 * ContentContext) resolve every user-facing string up front via `buildRetailInvoiceLabels`
 * and pass the resulting object into `<RetailInvoicePDF labels={...} />`.
 *
 * Each field's ContentId carries the EXACT current English invoice text as its
 * defaultValue, so rendered English output is byte-identical to before localization.
 *
 * NOTE on ContentId selection: `getContent` always returns a resolved string for a
 * mapped ContentId (cache value, else the ContentId's own English defaultValue) — it
 * never returns empty for a mapped id. So the component-side `?? '<literal>'` fallback
 * only fires when `labels` is entirely absent. To guarantee identical English, each
 * label therefore uses a ContentId whose defaultValue equals the exact invoice literal.
 * Existing ContentIds are reused ONLY where their English default already matches the
 * invoice text verbatim; where the invoice uses a different form (e.g. "Invoice #" vs
 * the shared "Invoice Number", "FROM" vs "From", "Proceed ($)" vs "Proceed"), a
 * dedicated invoice-specific ContentId carries the exact literal instead.
 */
export interface RetailInvoiceLabels {
  // Header
  invoiceTitle: string;
  totalInvoiceValue: string;
  paid: string;
  // Metadata
  invoiceNumber: string;
  invoiceDate: string;
  paymentTerms: string;
  currency: string;
  notAvailable: string;
  // Address
  from: string;
  billedTo: string;
  // Order detail
  orderDetail: string;
  event: string;
  section: string;
  row: string;
  seatRange: string;
  qty: string;
  proceedHeader: string;
  overview: string;
  ratePercent: string;
  amountHeader: string;
  subtotal: string;
  totalProceed: string;
  payment: string;
  balanceDue: string;
  refPrefix: string;
  // Payment info
  paymentInfoAndTerms: string;
  paymentMethod: string;
  instructions: string;
  payingRemainingBalance: string;
  paymentReference: string;
  refundPolicy: string;
  allSalesFinal: string;
  // Broker notes
  brokerNotes: string;
  // POS-8856: invoice DATA-builder line-item/type labels (used by the callers that
  // assemble lineItems/costType strings, e.g. buildGroupInvoiceProps and the retail
  // download/preview paths), not by the PDF chrome above.
  proceeds: string;
  charge: string;
  credit: string;
  ticketProceeds: string;
}

export function buildRetailInvoiceLabels(
  contentContext: IContentContext
): RetailInvoiceLabels {
  return {
    // Header
    invoiceTitle: getContent(ContentId.InvoiceTitle, contentContext),
    totalInvoiceValue: getContent(ContentId.TotalInvoiceValue, contentContext),
    paid: getContent(ContentId.Paid, contentContext),
    // Metadata
    // Invoice header uses "Invoice #" (differs from shared ContentId.InvoiceNumber
    // = "Invoice Number"), so a dedicated ContentId carries the exact literal.
    invoiceNumber: getContent(ContentId.InvoiceNumberSymbol, contentContext),
    invoiceDate: getContent(ContentId.InvoiceDate, contentContext),
    paymentTerms: getContent(ContentId.PaymentTerms, contentContext),
    currency: getContent(ContentId.Currency, contentContext),
    notAvailable: getContent(ContentId.NA, contentContext),
    // Address
    // "FROM" (all caps) differs from shared ContentId.From = "From".
    from: getContent(ContentId.FromUppercase, contentContext),
    billedTo: getContent(ContentId.BilledTo, contentContext),
    // Order detail
    orderDetail: getContent(ContentId.OrderDetail, contentContext),
    event: getContent(ContentId.Event, contentContext),
    section: getContent(ContentId.Section, contentContext),
    row: getContent(ContentId.Row, contentContext),
    // "Seat Range" (title case) differs from shared ContentId.SeatRange = "Seat range".
    seatRange: getContent(ContentId.SeatRangeHeader, contentContext),
    qty: getContent(ContentId.QuantityAbbreviated, contentContext),
    // "Proceed ($)" differs from shared ContentId.Proceed = "Proceed".
    proceedHeader: getContent(ContentId.ProceedHeader, contentContext),
    overview: getContent(ContentId.Overview, contentContext),
    ratePercent: getContent(ContentId.RatePercent, contentContext),
    // "Amount $" differs from shared ContentId.Amount = "Amount".
    amountHeader: getContent(ContentId.AmountHeader, contentContext),
    subtotal: getContent(ContentId.Subtotal, contentContext),
    totalProceed: getContent(ContentId.TotalProceed, contentContext),
    payment: getContent(ContentId.Payment, contentContext),
    balanceDue: getContent(ContentId.BalanceDue, contentContext),
    refPrefix: getContent(ContentId.RefPrefix, contentContext),
    // Payment info
    paymentInfoAndTerms: getContent(
      ContentId.PaymentInfoAndTerms,
      contentContext
    ),
    paymentMethod: getContent(ContentId.PaymentMethod, contentContext),
    instructions: getContent(ContentId.Instructions, contentContext),
    payingRemainingBalance: getContent(
      ContentId.PayingRemainingBalance,
      contentContext
    ),
    paymentReference: getContent(ContentId.PaymentReference, contentContext),
    refundPolicy: getContent(ContentId.RefundPolicy, contentContext),
    allSalesFinal: getContent(ContentId.AllSalesFinal, contentContext),
    // Broker notes
    brokerNotes: getContent(ContentId.BrokerNotes, contentContext),
    // POS-8856: invoice DATA-builder line-item/type labels (line-item descriptions and
    // charge/credit cost-type strings assembled by the invoice-props callers).
    proceeds: getContent(ContentId.Proceeds, contentContext),
    charge: getContent(ContentId.Charge, contentContext),
    credit: getContent(ContentId.Credit, contentContext),
    ticketProceeds: getContent(ContentId.TicketProceeds, contentContext),
  };
}
